import { Component, ErrorInfo, ReactNode } from "react";

import Bugsnag from "@bugsnag/js";
import { env } from "next-runtime-env";

import styles from "./ErrorBoundary.module.scss";

interface State {
  action?: ReactNode;
  error?: Error | null;
  fullPage?: boolean;
  subtitle?: ReactNode;
  title?: ReactNode;
}

interface Props {
  children?: ReactNode;
  component?: (state: State) => ReactNode;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (env("NEXT_PUBLIC_BUGSNAG_API_KEY")) {
      Bugsnag.notify(error, (event) => {
        event.addMetadata("ErrorInfo", errorInfo);
        event.severity = "error";
        event.unhandled = true;
      });
    }
  }

  public render() {
    if (this.state.error) {
      if (this.props.component) {
        return this.props.component(this.state);
      }

      return <GenericError fullPage {...this.state} />;
    }

    return this.props.children;
  }
}

export function GenericError(props: State) {
  const panel = (
    <div className={styles.container}>
      <h1 className={styles.title}>{props.title || "An error occurred"}</h1>

      <p className={styles.subtitle}>
        {props.subtitle ||
          "Sorry about this. In case anyone asks, the error was:"}
      </p>

      {props.error && (
        <code className={styles.errorContainer}>
          <pre className={styles.error}>{props.error.message}</pre>
        </code>
      )}

      {props.action && (
        <div className={styles.actionContainer}>{props.action}</div>
      )}
    </div>
  );

  if (props.fullPage) {
    return <main className={styles.main}>{panel}</main>;
  }

  return panel;
}
